const STORAGE_KEY = 'rb';

/**
 * Fetches the value of a specific attribute from the "rb" item in localStorage.
 * @param {string} key - The name of the attribute to fetch.
 * @returns {*} The value of the attribute, or null if not found.
 */
export const getItem = (key) => {
    try {
        const data = localStorage.getItem(STORAGE_KEY);
        if (!data) return null;

        const parsedData = JSON.parse(data);
        return parsedData[key] ?? null;
    } catch (error) {
        console.error('Failed to fetch attribute from localStorage:', error);
        return null;
    }
};

/**
 * Sets or updates a specific attribute in the "rb" item in localStorage.
 * @param {string} key - The name of the attribute to set or update.
 * @param {*} value - The value to set for the attribute.
 */
export const setItem = (key, value) => {
    try {
        const data = localStorage.getItem(STORAGE_KEY);
        const parsedData = data ? JSON.parse(data) : {};

        if (typeof parsedData !== 'object') {
            throw new Error('Invalid data in localStorage. Expected an object.');
        }

        // Update the attribute and save back to localStorage
        parsedData[key] = value;
        localStorage.setItem(STORAGE_KEY, JSON.stringify(parsedData));
    } catch (error) {
        console.error('Failed to update attribute in localStorage:', error);
    }
};


/**
 * Fetches the complete "rb" object from localStorage.
 * @returns {object|null} The full object or null if not found or invalid.
 */
export const getStore = () => {
    try {
        const data = localStorage.getItem(STORAGE_KEY);
        return data ? JSON.parse(data) : null;
    } catch (error) {
        console.error('Failed to fetch object from localStorage:', error);
        return null;
    }
};

/**
 * Overwrites the "rb" object in localStorage.
 * @param {object} value - The full object to store. Must include `isoCountry`, `env`, and `lang`.
 * @throws {Error} If the value is not valid.
 */
export const setStore = (value) => {
    if (!isValidItem(value)) {
        throw new Error('Invalid rb object. Must include isoCountry, env, and lang.');
    }
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
    } catch (error) {
        console.error('Failed to store object in localStorage:', error);
    }
};

/**
 * Validates the structure of the "rb" object.
 * @param {object} value - The object to validate.
 * @returns {boolean} True if valid, false otherwise.
 */
const isValidItem = (value) => {
    return (
        value &&
        typeof value === 'object' &&
        typeof value.isoCountry === 'string' &&
        typeof value.env === 'string' &&
        typeof value.lang === 'string'
    );
};